import { useEffect, useState } from 'react'
import { Plus, FolderOpen, Fish, Pencil, Trash2 } from 'lucide-react'
import type { Project } from '@shared/types'
import { useEditorStore } from '../../store/editorStore'
import { Button } from '../common/Button'
import { ConfirmDialog } from '../common/ConfirmDialog'
import { Menu, MenuItem, MenuPanel } from '../common/Menu'
import { EmptyState, ErrorState, LoadingState } from '../common/States'

function timeAgo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diffMs / 60000)
  if (mins < 1) return 'たった今'
  if (mins < 60) return `${mins}分前`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}時間前`
  return `${Math.floor(hours / 24)}日前`
}

/** P10-2 (docs/P10_PHASE1.md §3 item 8) inline rename — Enter confirms, Esc cancels, empty is rejected (falls back to the original name instead of saving blank). */
function RenameInput({ project, onDone }: { project: Project; onDone: () => void }) {
  const renameProject = useEditorStore((s) => s.renameProject)
  const [value, setValue] = useState(project.name)
  const [saving, setSaving] = useState(false)

  async function commit() {
    const trimmed = value.trim()
    if (!trimmed || trimmed === project.name) {
      onDone()
      return
    }
    setSaving(true)
    try {
      await renameProject(project.id, trimmed)
    } catch {
      // best-effort: rename failing (network hiccup, 404 if the project was
      // deleted from another tab) just reverts to the pre-rename name —
      // loadProjects's own refetch on next mount would show the real state.
    }
    onDone()
  }

  return (
    <input
      autoFocus
      data-testid="project-rename-input"
      value={value}
      disabled={saving}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          commit()
        } else if (e.key === 'Escape') {
          e.preventDefault()
          onDone()
        }
      }}
      className="w-full rounded bg-ink-bg/70 border border-accent-blue/60 px-1.5 py-0.5 text-sm text-text focus:outline-none"
    />
  )
}

export function ProjectList() {
  const { projects, projectsLoading, projectsError, loadProjects, createProject, openProject, deleteProject } =
    useEditorStore()
  const [creating, setCreating] = useState(false)
  const [name, setName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [deletingProject, setDeletingProject] = useState<Project | null>(null)

  useEffect(() => {
    loadProjects()
  }, [loadProjects])

  async function handleCreate() {
    const trimmed = name.trim() || '新規プロジェクト'
    setSubmitting(true)
    await createProject(trimmed)
    setSubmitting(false)
    setCreating(false)
    setName('')
  }

  return (
    <div className="min-h-screen bg-ink-bg text-text">
      <header className="flex items-center justify-between px-8 py-6 border-b border-ink-border">
        <div className="flex items-center gap-2.5">
          <Fish size={22} className="text-accent-red" strokeWidth={2.2} />
          <span className="text-lg font-bold tracking-wide">AQUACUT</span>
          <span className="text-xs text-text-faint ml-1">アートアクアリウム 編集自動化</span>
        </div>
        <Button variant="primary" icon={<Plus size={16} />} onClick={() => setCreating(true)}>
          新規プロジェクト
        </Button>
      </header>

      <main className="max-w-5xl mx-auto px-8 py-10">
        {creating && (
          <div className="mb-8 p-5 rounded bg-ink-surface border border-ink-border">
            <div className="text-sm font-medium mb-3">新規プロジェクト</div>
            <div className="flex gap-2">
              <input
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                placeholder="例: 銀座_初夏の企画展"
                className="flex-1 rounded bg-ink-bg/70 border border-ink-border px-3 py-2 text-sm focus:outline-none focus:border-accent-blue/60"
              />
              <Button variant="primary" onClick={handleCreate} disabled={submitting}>
                {submitting ? '作成中…' : '作成'}
              </Button>
              <Button variant="ghost" onClick={() => setCreating(false)}>
                キャンセル
              </Button>
            </div>
          </div>
        )}

        {projectsLoading && <LoadingState label="プロジェクトを読み込み中…" />}
        {!projectsLoading && projectsError && (
          <ErrorState message={projectsError} onRetry={loadProjects} />
        )}
        {!projectsLoading && !projectsError && projects.length === 0 && (
          <EmptyState
            icon={<FolderOpen size={32} />}
            title="プロジェクトがありません"
            description="撮影済みのクリップを投入して、開幕告知型の動画を自動生成できます。"
            action={
              <Button
                variant="primary"
                size="sm"
                icon={<Plus size={14} />}
                className="mt-2"
                onClick={() => setCreating(true)}
              >
                最初のプロジェクトを作成
              </Button>
            }
          />
        )}

        {!projectsLoading && !projectsError && projects.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((p) => (
              <div
                key={p.id}
                role="button"
                tabIndex={0}
                data-testid="project-card"
                onClick={() => renamingId !== p.id && openProject(p.id)}
                onKeyDown={(e) => {
                  if (renamingId === p.id) return
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    openProject(p.id)
                  }
                }}
                className="relative text-left p-4 rounded bg-ink-surface border border-ink-border hover:border-accent-blue/50 hover:shadow-glow transition-all group cursor-pointer"
              >
                <div
                  className="absolute top-2 right-2 z-10"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <Menu
                    testId="project-menu"
                    trigger={({ toggle }) => (
                      <button
                        type="button"
                        onClick={toggle}
                        className="w-6 h-6 rounded flex items-center justify-center text-text-faint hover:text-text hover:bg-white/10 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                        aria-label="プロジェクトメニュー"
                        data-testid="project-menu-trigger"
                      >
                        <span className="text-base leading-none">⋯</span>
                      </button>
                    )}
                  >
                    {({ close }) => (
                      <MenuPanel>
                        <MenuItem
                          icon={<Pencil size={13} />}
                          testId="project-menu-rename"
                          onSelect={() => {
                            close()
                            setRenamingId(p.id)
                          }}
                        >
                          名前を変更
                        </MenuItem>
                        <MenuItem
                          icon={<Trash2 size={13} />}
                          danger
                          testId="project-menu-delete"
                          onSelect={() => {
                            close()
                            setDeletingProject(p)
                          }}
                        >
                          削除
                        </MenuItem>
                      </MenuPanel>
                    )}
                  </Menu>
                </div>

                <div className="w-full aspect-[9/16] max-h-32 rounded bg-ink-bg mb-3 flex items-center justify-center overflow-hidden">
                  <Fish size={28} className="text-ink-border group-hover:text-accent-blue/40 transition-colors" />
                </div>
                {renamingId === p.id ? (
                  <RenameInput project={p} onDone={() => setRenamingId(null)} />
                ) : (
                  <div className="text-sm font-medium truncate">{p.name}</div>
                )}
                <div className="text-[11px] text-text-faint mt-1">
                  {p.assetIds.length}素材 · 構成案{p.compositionIds.length}件 · 更新 {timeAgo(p.updatedAt)}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {deletingProject && (
        <ConfirmDialog
          title={`『${deletingProject.name}』を削除しますか？`}
          message={`素材 ${deletingProject.assetIds.length} 件・構成案 ${deletingProject.compositionIds.length} 件と書き出しファイルも削除されます。元に戻せません。`}
          busyMessage="取り込み／書き出し中のため削除できません"
          onCancel={() => setDeletingProject(null)}
          onConfirm={async () => {
            await deleteProject(deletingProject.id)
            setDeletingProject(null)
          }}
        />
      )}
    </div>
  )
}
